import { gql } from '@apollo/client'
import { Col, Container, Row, Separator } from '@farol-ds/react'
import { Footer } from '@jusbrasil/footer'
import { Topbar, TopbarCurrentNavKey } from '@jusbrasil/topbar'
import { BifrostModalTrigger } from '@jusbrasil-web/shared-bifrost'
import { useGTM } from '@jusbrasil-web/shared-gtm'
import classNames from 'classnames'
import { ReactNode, RefObject, useEffect } from 'react'

import layoutStyles from '../../layout.module.scss'
import { MetricsContext, MetricsProperties } from '../../metrics'
import printStyles from '../../print.module.scss'
import { DocContextMenu } from '../doc-context-menu'
import { DocPopover, DocPopoverElement, EntityType } from '../doc-popover'
import { DocPrintable } from '../doc-printable'
import {
  DocviewLite_DocumentFragment,
  DocviewLite_ViewerFragment,
} from './__generated__/docview-lite.graphql'
import styles from './docview-lite.module.scss'

type LayoutProps = {
  header: ReactNode
  mainContent: ReactNode
  relatedContent?: ReactNode
  navigationPrevious?: ReactNode
  navigationNext?: ReactNode
}

export type DocviewLiteProps = {
  document: DocviewLite_DocumentFragment
  viewer: DocviewLite_ViewerFragment | null
  metricsProperties: MetricsProperties
  canCopyContent: boolean
  disableTouchFeatures: boolean
  handleCopy?: (event: React.ClipboardEvent<HTMLDivElement>) => void
  dispatcherRef: RefObject<HTMLDivElement>
  contentRef: RefObject<HTMLDivElement>
  topbarCurrentNavKey?: TopbarCurrentNavKey
  docPopoverRef?: RefObject<DocPopoverElement>
  afterTopbar?: ReactNode
  endContent?: ReactNode
  metaTags?: ReactNode
  contextMenu?: ReactNode
  popoverEntityType?: EntityType
  copyRef?: RefObject<HTMLDivElement>
} & (
  | ({ children: ReactNode } & { [P in keyof LayoutProps]?: never })
  | ({ children?: never } & LayoutProps)
)

export function DocviewLite(props: DocviewLiteProps) {
  const {
    document,
    viewer,
    metricsProperties,
    afterTopbar,
    header,
    mainContent,
    relatedContent,
    endContent,
    canCopyContent,
    disableTouchFeatures,
    handleCopy,
    dispatcherRef,
    contentRef,
    topbarCurrentNavKey,
    docPopoverRef,
    metaTags,
    navigationPrevious,
    navigationNext,
    contextMenu = <DocContextMenu />,
    popoverEntityType,
    copyRef,
  } = props

  const { children } = props
  const gtm = useGTM()
  useEffect(() => {
    gtm.push({ ga4_resource_id: document.boxResourceId })
  }, [document.boxResourceId, gtm])

  const renderChildren = () => {
    if (children) {
      return (
        <div ref={dispatcherRef} onCopy={handleCopy}>
          {children}
        </div>
      )
    }

    return (
      <Container
        ref={dispatcherRef}
        className={classNames(layoutStyles.container, layoutStyles.anchorWrapper)}
        onCopy={handleCopy}
      >
        <Row>
          <Col xs={0} md={2}>
            {navigationPrevious}
          </Col>

          <Col xs={12} md={8} asChild>
            <main ref={copyRef}>
              <div className={classNames(styles.main, layoutStyles.mainContent)}>
                {header}
                {mainContent}
                {relatedContent && <Separator className={layoutStyles.separator} />}
                {relatedContent}
              </div>
            </main>
          </Col>

          <Col xs={0} md={2}>
            {navigationNext}
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <MetricsContext.Provider value={metricsProperties}>
      {contextMenu}
      {metaTags}

      <Topbar currentNavKey={topbarCurrentNavKey} className={printStyles.unprintable} />

      {afterTopbar}

      <DocPrintable viewer={viewer} document={document} canCopy={canCopyContent}>
        {renderChildren()}
      </DocPrintable>

      <Footer className={printStyles.unprintable} />
      <BifrostModalTrigger viewer={viewer} />
      <DocPopover
        contentRef={contentRef}
        disableTouchFeatures={disableTouchFeatures}
        ref={docPopoverRef}
        hasLRSubscription={!!viewer?.hasLegalResearch}
        popoverEntityType={popoverEntityType}
      />

      {endContent}
    </MetricsContext.Provider>
  )
}

DocviewLite.fragments = {
  document: gql`
    fragment DocviewLite_document on Document {
      boxResourceId
      ...DocPrintable_document
    }

    ${DocPrintable.fragments.document}
  `,

  viewer: gql`
    fragment DocviewLite_viewer on Profile {
      ...BifrostModalTrigger_viewer
      ...DocPrintable_viewer
      hasLegalResearch
    }

    ${BifrostModalTrigger.fragments.viewer}
    ${DocPrintable.fragments.viewer}
  `,
}
